import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { QUOTE_STATUSES, DISPATCH_STATUSES, statusMeta, dispatchMeta, formatMoney, relativeTime } from "@/lib/quotes";
import { Plus, Search, Package, ArrowRight, Loader2, User, Phone, MapPin, Truck, X, CalendarDays, Filter } from "lucide-react";
import { toast } from "sonner";

export default function QuotesListPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isSales = role === "sales_team" || role === "admin";
  const isMgmtOrAdmin = role === "management" || role === "admin";
  const isDispatch = role === "dispatch_team";
  const [searchParams, setSearchParams] = useSearchParams();

  const urlView = searchParams.get("view");
  const dispatchMode = urlView === "dispatch" || isDispatch;

  // Filters (persisted in the URL so drill-ins from the dashboard link cleanly)
  const urlStatus = searchParams.get("status") || "all";
  const urlDispatch = searchParams.get("dispatch_status") || "all";
  const urlUserId = searchParams.get("user_id") || "";
  const urlStart = searchParams.get("start_date") || "";
  const urlEnd = searchParams.get("end_date") || "";

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState(urlStart);
  const [endDate, setEndDate] = useState(urlEnd);

  // Sync local date pickers when URL params change
  useEffect(() => { setStartDate(urlStart); setEndDate(urlEnd); }, [urlStart, urlEnd]);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dispatchMode && urlDispatch !== "all") p.set("dispatch_status", urlDispatch);
      if (!dispatchMode && urlStatus !== "all") p.set("status", urlStatus);
      if (isMgmtOrAdmin && urlUserId) p.set("user_id", urlUserId);
      if (urlStart) p.set("start_date", urlStart);
      if (urlEnd) p.set("end_date", urlEnd);
      const { data } = await api.get(`/quotes${p.toString() ? "?" + p.toString() : ""}`);
      setQuotes(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [urlStatus, urlDispatch, urlUserId, urlStart, urlEnd, dispatchMode]);

  const setParam = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "" || v === "all") next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };

  const applyDates = () => setParam({ start_date: startDate, end_date: endDate });
  const clearDates = () => { setStartDate(""); setEndDate(""); setParam({ start_date: null, end_date: null }); };

  const filtered = useMemo(() => {
    if (!query.trim()) return quotes;
    const q = query.trim().toLowerCase();
    return quotes.filter((x) =>
      (x.quote_no || "").toLowerCase().includes(q) ||
      (x.customer?.name || "").toLowerCase().includes(q) ||
      (x.customer?.phone || "").toLowerCase().includes(q)
    );
  }, [quotes, query]);

  const focusedSalesName = useMemo(() => {
    if (!urlUserId) return null;
    const hit = quotes.find((q) => q.created_by === urlUserId);
    return hit?.created_by_name || urlUserId;
  }, [urlUserId, quotes]);

  const title = dispatchMode ? "Dispatch queue" : (isSales && !isMgmtOrAdmin ? "My quotations" : "All quotations");
  const subtitle = dispatchMode
    ? "Won quotations moving through your desk"
    : (isSales && !isMgmtOrAdmin ? "Every deal you've sent, in one place" : "Every quotation across the sales team");

  const activeChips = [
    !dispatchMode && urlStatus !== "all" && { key: "status", label: `Status: ${statusMeta(urlStatus).label}`, onClear: () => setParam({ status: null }) },
    dispatchMode && urlDispatch !== "all" && { key: "dispatch_status", label: `Dispatch: ${dispatchMeta(urlDispatch).label}`, onClear: () => setParam({ dispatch_status: null }) },
    urlUserId && { key: "user_id", label: `Salesperson: ${focusedSalesName || urlUserId}`, onClear: () => setParam({ user_id: null }) },
    urlStart && { key: "from", label: `From ${urlStart}`, onClear: () => { setStartDate(""); setParam({ start_date: null }); } },
    urlEnd && { key: "to", label: `To ${urlEnd}`, onClear: () => { setEndDate(""); setParam({ end_date: null }); } },
  ].filter(Boolean);

  return (
    <div className="px-4 pt-6 pb-4" data-testid="quotes-list-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-mono-mini text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {dispatchMode ? "Dispatch" : "Quotations"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{title}</h1>
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
        {isSales && !dispatchMode && (
          <Link
            to="/quotes/new"
            data-testid="quotes-new-fab"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium btn-lift shadow-soft"
            style={{ background: "hsl(var(--status-leave))" }}
          >
            <Plus className="h-4 w-4" /> New
          </Link>
        )}
      </div>

      {/* Search + filters */}
      <div className="mt-4 flex flex-col gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="quotes-search"
            placeholder="Search quote no., customer name, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10"
          />
        </div>

        {!dispatchMode ? (
          <ToggleGroup
            type="single"
            value={urlStatus}
            onValueChange={(v) => { if (v) setParam({ status: v }); }}
            className="w-full grid grid-cols-5 border border-border rounded-md overflow-hidden"
          >
            <ToggleGroupItem value="all" data-testid="filter-all" className="h-9 text-xs rounded-none border-0 data-[state=on]:bg-foreground data-[state=on]:text-background">All</ToggleGroupItem>
            {QUOTE_STATUSES.map((s) => (
              <ToggleGroupItem
                key={s.id}
                value={s.id}
                data-testid={`filter-${s.id}`}
                className="h-9 text-xs rounded-none border-0 capitalize data-[state=on]:text-white"
                style={urlStatus === s.id ? { background: s.color } : undefined}
              >
                {s.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        ) : (
          <ToggleGroup
            type="single"
            value={urlDispatch}
            onValueChange={(v) => { if (v) setParam({ dispatch_status: v }); }}
            className="w-full grid grid-cols-3 sm:grid-cols-6 border border-border rounded-md overflow-hidden"
          >
            <ToggleGroupItem value="all" data-testid="dispatch-filter-all" className="h-9 text-xs rounded-none border-0 data-[state=on]:bg-foreground data-[state=on]:text-background">All</ToggleGroupItem>
            {DISPATCH_STATUSES.map((s) => (
              <ToggleGroupItem
                key={s.id}
                value={s.id}
                data-testid={`dispatch-filter-${s.id}`}
                className="h-9 text-xs rounded-none border-0"
                style={urlDispatch === s.id ? { background: s.color, color: "white" } : undefined}
              >
                {s.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}

        {/* Date range — available to management/admin, dispatch */}
        {(isMgmtOrAdmin || isDispatch) && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">From</Label>
              <Input
                type="date"
                data-testid="list-start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 h-9 font-mono-mini w-[150px]"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">To</Label>
              <Input
                type="date"
                data-testid="list-end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 h-9 font-mono-mini w-[150px]"
              />
            </div>
            <Button
              onClick={applyDates}
              data-testid="list-apply-dates"
              className="h-9 btn-lift"
              variant="outline"
              size="sm"
            >
              <Filter className="h-3.5 w-3.5 mr-1.5" /> Apply
            </Button>
            {(urlStart || urlEnd) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDates}
                data-testid="list-clear-dates"
                className="h-9 text-muted-foreground"
              >
                Clear
              </Button>
            )}
          </div>
        )}

        {/* Applied filter chips */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="active-filter-chips">
            {activeChips.map((c) => (
              <button
                key={c.key}
                onClick={c.onClear}
                data-testid={`chip-${c.key}`}
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border border-border bg-card hover:bg-black/5 transition-colors"
              >
                <CalendarDays className="h-3 w-3 text-muted-foreground" />
                {c.label}
                <X className="h-3 w-3 opacity-70" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-4 space-y-3" data-testid="quotes-list">
        {loading ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-8 rounded-2xl border-dashed border-2 border-border text-center shadow-none">
            <Package className="h-6 w-6 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">
              {quotes.length === 0
                ? (isSales ? "No quotations matching the filters." : "No quotations yet.")
                : "No matches for these filters."}
            </div>
            {isSales && quotes.length === 0 && activeChips.length === 0 && (
              <Link
                to="/quotes/new"
                data-testid="empty-new-quote-cta"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-xl text-white text-sm font-medium btn-lift shadow-soft"
                style={{ background: "hsl(var(--status-leave))" }}
              >
                New quotation <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </Card>
        ) : (
          filtered.map((q) => {
            const s = statusMeta(q.status);
            const d = q.dispatch ? dispatchMeta(q.dispatch.status) : null;
            return (
              <Link
                key={q.quote_id}
                to={`/quotes/${q.quote_id}`}
                data-testid={`quote-row-${q.quote_id}`}
                className="block p-4 rounded-md border border-border bg-card btn-lift hover:shadow-[0_2px_0_hsl(var(--border))] transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-mini text-[11px] tabular-nums text-muted-foreground">{q.quote_no}</span>
                      <span
                        className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
                        style={{ background: s.bg, color: s.color }}
                      >
                        {s.label}
                      </span>
                      {d && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-border"
                          style={{ color: d.color }}
                        >
                          <Truck className="h-2.5 w-2.5" /> {d.label}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-sm font-medium truncate">{q.customer?.name}</div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1 truncate">
                        <Phone className="h-3 w-3" /> {q.customer?.phone}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground truncate">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{q.customer?.address}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <User className="h-3 w-3" /> {q.created_by_name || "—"} · {relativeTime(q.created_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    {q.final_price != null && (
                      <div className="font-display text-lg font-bold tabular-nums" style={{ color: s.color }}>
                        {formatMoney(q.final_price)}
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                      {q.items?.length ?? 0} items
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
